import React, { useCallback } from "react"
import { Trans } from "@lingui/macro"
import { useTotalPrice, UseTotalPriceResponse } from "../Sidebar/useTotalPrice"
import useModal from "../ModalProvider/useModal"
import { FetchPageContent } from "../../app-core/_common/sanity/fetch-page-content"
import NextButton from "../NextButton/NextButton"
import { useECommerceMachine } from "../../app-core/_common/machines/machines-context"
import { WebQuoteExitIntent } from "../../app-core/v3/_common/web-quote-exit-intent"
import PriceBox from "./PriceBox/PriceBox"
import styles from "./BottomBar.module.scss"

type Props = {
    className?: string
    sanityContent: FetchPageContent
    onNextClick: () => void
    isNextEnabled: boolean
    showNextButton: boolean
    webQuoteExitIntent: WebQuoteExitIntent
}

const BottomBar: React.FC<Props> = ({
    isNextEnabled,
    onNextClick,
    sanityContent,
    showNextButton,
    webQuoteExitIntent,
}) => {
    const [state] = useECommerceMachine()
    const { openModal } = useModal()
    const onOpenModal = useCallback(() => {
        openModal({ modalType: "receipt", sanityContent, webQuoteExitIntent })
    }, [openModal, sanityContent])

    const totalPrice = useTotalPrice({ extendedLabel: true })

    const automaticSelectionOfSuiteNumberInProgress =
        state.context.flowVersion.ui.automaticSelectionOfSuiteNumberInProgress(state)

    return (
        <aside className={styles.bottomBar} data-content-name="bottom-bar" data-test-id="bottom-bar">
            <div className={styles.floatingFaqLink}>
                {state.context.flowVersion.ui.faq.FloatingButton(state, "bottom-bar")}
            </div>

            <div className={styles.main}>
                <div className={styles.priceContainer}>
                    {totalPrice && <PriceBoxWithFarePrice totalPrice={totalPrice} onDetailsClick={onOpenModal} />}
                </div>
                <div>
                    {showNextButton && (
                        <NextButton
                            onClick={onNextClick}
                            disabled={!isNextEnabled}
                            loading={automaticSelectionOfSuiteNumberInProgress}
                        />
                    )}
                </div>
            </div>

            {state.context.flowVersion.ui.courtesyHoldBottomBar(state, { withDivider: !!totalPrice })}
        </aside>
    )
}

type PriceBoxWithFarePriceProps = {
    totalPrice: UseTotalPriceResponse
    onDetailsClick: () => void
}

const PriceBoxWithFarePrice: React.FC<React.PropsWithChildren<PriceBoxWithFarePriceProps>> = ({
    totalPrice,
    onDetailsClick,
}) => {
    if (!totalPrice) return null

    const { formattedAmount, formattedOriginalAmount, price } = totalPrice || {}

    return (
        <PriceBox
            topLabel={price.label}
            amount={formattedAmount}
            originalAmount={formattedOriginalAmount}
            buttonDataContentPiece="button-open"
            onToggleOpen={onDetailsClick}
            status={price.status}
            toggleButtonLabel={<Trans>Price details</Trans>}
        />
    )
}

export default BottomBar
